import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Check, Database, Eye, EyeOff, Loader2, Server, Unlink, X } from "lucide-react";
import { useI18n, serverError } from "../i18n";

/**
 * 一個可設定的金鑰欄位
 * ---------------------------------------------------------------
 *
 * 這個元件最重要的設計決定是：**沒有「顯示明文」的按鈕**。
 *
 * 值寫進去之後就再也讀不回來，畫面上只有遮罩預覽（re_••••••Wss）。
 * 這不是介面上的疏漏，而是刻意的 ——
 * 管理員帳號被盜時，攻擊者能做的最壞的事是把金鑰換掉
 * （會被稽核日誌記下），而不是安靜地把金鑰複製走。
 *
 * 遮罩保留頭尾幾碼，足以讓管理員確認「這是我設的那把」。
 */

export interface SettingFieldData {
  key: string;
  label: string;
  configured: boolean;
  /** database = 後台設定的；env = Worker 環境變數；none = 沒設 */
  source: "database" | "env" | "none";
  preview: string | null;
  secret?: boolean;
  required?: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export function SettingField({
  field,
  endpoint,
  hint,
  placeholder,
  options,
  revocable,
  suggestions,
  onSaved,
}: {
  field: SettingFieldData;
  /** PUT 的目標，例如 /settings/email */
  endpoint: string;
  hint?: string;
  placeholder?: string;
  /** 有值時渲染成下拉選單而不是輸入框 */
  options?: { value: string; label: string }[];
  /**
   * 可不可以解除綁定。
   *
   * ── 為什麼不是每個欄位都能解除 ──────────────────────────────
   *
   * Resend 的金鑰解除掉，全站的驗證信就寄不出去 —— 那不是一個設定
   * 選項，那是把功能關掉。那些欄位只能「換一把」。
   *
   * 而 OpenRouter 是**可有可無的綁定**：解除之後代理人退回平台自己
   * 的模型，功能照常。可以綁就必須可以解除，否則管理員一旦綁了就
   * 再也回不去 —— 他只能填一把壞掉的金鑰讓它失敗退回，
   * 而那是一個很糟的解法。
   */
  revocable?: boolean;
  /**
   * 可以點一下就填進輸入框的候選值。
   *
   * note 是給人看的補充（例如成本），不是值的一部分 ——
   * 點下去填進去的只有 value。
   */
  suggestions?: { value: string; note?: string }[];
  onSaved: () => void;
}) {
  const { t, locale } = useI18n();
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [revealed, setRevealed] = useState(false);
  /*
    解除要按兩下。

    不用彈窗 —— 確認彈窗會被習慣性地按掉，而按鈕自己變成
    「確定解除？」擋得住手滑，又不會打斷正在做事的人。
  */
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  async function revoke() {
    setSaving(true);
    setResult(null);
    try {
      const res = await fetch(`/api${endpoint}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        /* 空字串就是解除。伺服器端會把那一列刪掉 */
        body: JSON.stringify({ key: field.key, value: "" }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverError(payload, t, `HTTP ${res.status}`));

      setValue("");
      setConfirmRevoke(false);
      setResult({ ok: true, message: t("sf.revoked") });
      onSaved();
    } catch (error) {
      setResult({ ok: false, message: (error as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    if (!value.trim()) return;
    setSaving(true);
    setResult(null);

    try {
      const res = await fetch(`/api${endpoint}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ key: field.key, value: value.trim() }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverError(payload, t, `HTTP ${res.status}`));

      setValue("");
      setResult({ ok: true, message: t("sf.saved") });
      onSaved();
    } catch (error) {
      setResult({ ok: false, message: (error as Error).message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-2xl nm-inset-sm p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink">
          {field.label}
          {field.required && !field.configured && (
            <span className="ml-2 text-[11px] font-normal text-brand-300">{t("common.required")}</span>
          )}
        </span>
        <StatusChip field={field} />
      </div>

      {field.preview && (
        <p className="mt-2 flex items-center gap-2 font-mono text-xs text-ink-soft">
          <Eye className="size-3.5 shrink-0 text-ink-muted" />
          <span className="truncate">{field.preview}</span>
        </p>
      )}

      {hint && <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">{hint}</p>}

      {/*
        候選值。點一下填進輸入框，**不會**直接送出 ——
        他還要按儲存。少一次「點錯就上線了」。

        不做成 <select> 是因為那樣就只能選清單裡的，而這份清單
        一定會過期（模型會新增、會下架）—— 到那時候管理員連手動
        填一個新的都做不到。晶片是捷徑，輸入框仍然是主要的路。

        目前生效的那一個標起來，否則在一排長得很像的名字裡，
        他找不到自己上次選了哪一個。
      */}
      {suggestions && suggestions.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {suggestions.map((s) => {
            const active = s.value === field.preview;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => setValue(s.value)}
                className={
                  "flex items-baseline gap-1.5 rounded-lg px-2 py-1 transition-colors " +
                  (active
                    ? "nm-inset-sm text-ink"
                    : value === s.value
                      ? "nm-inset-sm text-brand-300"
                      : "nm-sm text-ink-muted hover:text-ink")
                }
              >
                <span className="font-mono text-[10.5px]">{s.value}</span>
                {s.note && <span className="font-sans text-[9.5px] opacity-70">{s.note}</span>}
                {active && <span className="font-sans text-[9.5px]">●</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {options ? (
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="min-w-0 flex-1 rounded-xl nm-inset bg-transparent px-3.5 py-2.5 text-sm text-ink outline-none"
          >
            <option value="">{t("sf.keepCurrent")}</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value} className="bg-surface text-ink">
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <div className="relative flex min-w-0 flex-1 items-center">
            {/*
              金鑰欄位不是 type="password"。
              ═══════════════════════════════════════════════════════

              這裡填的是 API 金鑰（Resend、PayPal Secret、綠界 HashKey），
              不是這個網站的登入密碼。用 type="password" 只是為了把字遮起來，
              但瀏覽器不知道我們的用意 —— 它會把這一格當成登入憑證，於是：

                · Chrome 警告「密碼欄位沒有包在 form 裡」
                · 密碼管理員跳出來問要不要把 PayPal 的 Secret
                  存成 admin.colouredev.com 的密碼

              第二點才是真的問題。把金流的 API 密鑰存進某人的密碼庫、
              跟著同步到手機和瀏覽器擴充，是我們沒打算做的事。

              改成 type="text" 加 CSS 遮蔽（-webkit-text-security）：
              視覺上一樣是圓點，但瀏覽器完全不把它當憑證看待。
              Chrome、Edge、Safari 與 Firefox 118+ 都支援。

              data-1p-ignore / data-lpignore 是給 1Password 與 LastPass
              的明示，讓它們連自動偵測都不要做。
            */}
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void save()}
              placeholder={placeholder ?? (field.configured ? t("sf.replaceHint") : t("sf.unset"))}
              autoComplete="off"
              spellCheck={false}
              data-1p-ignore
              data-lpignore="true"
              style={
                field.secret && !revealed
                  ? ({ WebkitTextSecurity: "disc", textSecurity: "disc" } as React.CSSProperties)
                  : undefined
              }
              className={
                "min-w-0 flex-1 rounded-xl nm-inset bg-transparent py-2.5 pl-3.5 font-mono text-sm text-ink outline-none placeholder:font-sans placeholder:text-ink-muted " +
                (field.secret ? "pr-10" : "pr-3.5")
              }
            />

            {/*
              看得到自己貼了什麼。

              金鑰又長又不能記，貼錯一個字元的結果是「儲存成功」
              但下一次交易失敗 —— 而那時候人已經不在這一頁了。
            */}
            {field.secret && (
              <button
                type="button"
                onClick={() => setRevealed((v) => !v)}
                aria-label={revealed ? t("sf.hide") : t("sf.reveal")}
                title={revealed ? t("sf.hide") : t("sf.reveal")}
                className="absolute right-2.5 flex size-6 items-center justify-center rounded-lg text-ink-muted hover:text-ink"
              >
                {revealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              </button>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !value.trim()}
          className="flex items-center gap-2 rounded-xl nm-pressable px-4 py-2.5 text-sm font-medium text-ink disabled:opacity-40"
        >
          {saving && <Loader2 className="size-3.5 animate-spin" />}
          {t("common.save")}
        </button>
      </div>

      {/*
        解除只在「後台設定的」時候出現。

        從環境變數來的值刪不掉 —— 資料庫裡根本沒有那一列，按下去
        什麼都不會發生，而管理員會以為自己解除了。
        那種按鈕比沒有按鈕糟。
      */}
      {revocable && field.configured && field.source === "database" && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => (confirmRevoke ? void revoke() : setConfirmRevoke(true))}
            disabled={saving}
            className={
              "flex items-center gap-2 rounded-xl nm-pressable px-3 py-2 text-xs font-medium disabled:opacity-40 " +
              (confirmRevoke ? "text-brand-300" : "text-ink-muted")
            }
          >
            <Unlink className="size-3.5" />
            {confirmRevoke ? t("sf.revokeConfirm") : t("sf.revoke")}
          </button>
          {confirmRevoke && (
            <button
              type="button"
              onClick={() => setConfirmRevoke(false)}
              className="rounded-xl px-3 py-2 text-xs text-ink-muted hover:text-ink"
            >
              {t("common.cancel")}
            </button>
          )}
        </div>
      )}

      <AnimatePresence>
        {result && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={
              "mt-2.5 flex items-start gap-2 text-xs leading-relaxed " +
              (result.ok ? "text-ink-soft" : "text-brand-300")
            }
          >
            {result.ok ? (
              <Check className="mt-0.5 size-3.5 shrink-0" />
            ) : (
              <X className="mt-0.5 size-3.5 shrink-0" />
            )}
            {result.message}
          </motion.p>
        )}
      </AnimatePresence>

      {field.updatedAt && (
        <p className="mt-2 text-[10px] text-ink-muted">
          {t("sf.updatedAt", { at: new Date(field.updatedAt).toLocaleString(locale) })}
        </p>
      )}
    </div>
  );
}

function StatusChip({ field }: { field: SettingFieldData }) {
  const { t } = useI18n();
  if (!field.configured) {
    return (
      <span className="rounded-pill bg-brand-600/26 px-2.5 py-1 text-[11px] font-medium text-brand-100">
        {t("sf.unset")}
      </span>
    );
  }

  /*
   * 分辨「後台設定的」與「部署時寫死的環境變數」很重要 ——
   * 前者可以在這裡改，後者要改程式碼重新部署。
   * 兩者長得一樣但維運方式完全不同。
   */
  const fromDb = field.source === "database";
  const Icon = fromDb ? Database : Server;

  return (
    <span className="flex items-center gap-1.5 rounded-pill nm-sm px-2.5 py-1 text-[11px] font-medium text-ink-soft">
      <Icon className="size-3" />
      {fromDb ? t("sf.fromDb") : t("sf.fromEnv")}
    </span>
  );
}

/**
 * 伺服器回的測試結果。
 *
 * key/params 是單一訊息；problems 是多則（綠界的格式檢查可能同時
 * 有好幾個問題）。兩者不會同時出現。
 */
interface TestResult {
  ok: boolean;
  key?: string;
  params?: Record<string, string | number>;
  problems?: { key: string; params?: Record<string, string | number> }[];
}

/**
 * params 裡凡是長得像翻譯 key 的值，先翻一層再帶進去。
 *
 * 伺服器送的 env 是 "env.sandbox" 而不是「測試」—— 那兩個字也要翻，
 * 而伺服器不知道要翻成哪一種語言。
 */
function useTv() {
  const { t } = useI18n();
  return (params?: Record<string, string | number>) => {
    if (!params) return undefined;
    const out: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(params)) {
      out[k] = typeof v === "string" && /^[a-z]+\.[a-zA-Z]+$/.test(v) ? t(v as never) : v;
    }
    return out;
  };
}

/** 「測試連線」按鈕。設定完能立刻確認有沒有生效，不用等真的有人付款。 */
export function TestButton({ endpoint, label }: { endpoint: string; label: string }) {
  const { t } = useI18n();
  const tv = useTv();
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  /*
    伺服器回的是 key + params，不是組好的句子。

    這一支的輸出顯示在管理台，而管理台有十八種語言 —— 回一整句
    中文的話，一個泰文介面的審核人員按下「測試」看到的是他讀不懂
    的字，那個按鈕對他等於不存在。
  */
  function render(data: TestResult): string {
    // 每一則各自翻譯之後再串起來
    if (data.problems) return data.problems.map((x) => t(x.key as never, tv(x.params))).join("；");
    return data.key ? t(data.key as never, tv(data.params)) : "";
  }

  async function run() {
    setTesting(true);
    setResult(null);
    try {
      const res = await fetch(`/api${endpoint}`, { method: "POST", credentials: "same-origin" });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverError(payload, t, `HTTP ${res.status}`));
      const data = payload.data as TestResult;
      setResult({ ok: data.ok, message: render(data) });
    } catch (error) {
      setResult({ ok: false, message: (error as Error).message });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => void run()}
        disabled={testing}
        className="flex items-center gap-2 rounded-xl nm-pressable px-4 py-2.5 text-sm font-medium text-ink disabled:opacity-40"
      >
        {testing && <Loader2 className="size-3.5 animate-spin" />}
        {label}
      </button>

      <AnimatePresence>
        {result && (
          <motion.p
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={
              "mt-3 flex items-start gap-2 rounded-xl nm-inset px-4 py-3 text-xs leading-relaxed " +
              (result.ok ? "text-ink-soft" : "text-brand-300")
            }
          >
            {result.ok ? (
              <Check className="mt-0.5 size-3.5 shrink-0" />
            ) : (
              <X className="mt-0.5 size-3.5 shrink-0" />
            )}
            {result.message}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

/** SETTINGS_KEY 沒設定時的說明。沒有主金鑰就無法加密儲存，整頁不能用。 */
export function EncryptionWarning({ service }: { service: string }) {
  const { t } = useI18n();
  return (
    <div className="rounded-card nm-inset p-6">
      <h3 className="text-sm font-semibold text-ink">{t("sf.noMasterKey")}</h3>
      <p className="mt-2.5 text-sm leading-relaxed text-ink-soft">{t("sf.noMasterKeyBody")}</p>
      <pre className="mt-3 overflow-x-auto rounded-xl nm-sm px-4 py-3 font-mono text-[11px] leading-relaxed text-ink-soft">
        {`openssl rand -base64 32 | npx wrangler secret put SETTINGS_KEY \\
  --cwd services/${service}`}
      </pre>
      <p className="mt-3 text-[11px] leading-relaxed text-ink-muted">
        {t("sf.rotateWarn")}
      </p>
    </div>
  );
}
